import { z } from 'zod';
import i18next from 'i18next';
import { http } from './http';
import { env } from '@/lib/env';

const meSchema = z.object({
  id: z.string(),
  full_name: z.string().nullish(),
  email: z.string().nullish(),
  wallet_balance: z.coerce.number().nullish(),
  role: z.string().nullish(),        // user_role (SSOT): admin|organizer|participant|…
  user_role: z.string().nullish(),
  // Perfil profissional/social
  username: z.string().nullish(),
  job_title: z.string().nullish(),
  company: z.string().nullish(),
  sector: z.string().nullish(),
  bio: z.string().nullish(),
  location: z.string().nullish(),
  phone: z.string().nullish(),
  avatar_url: z.string().nullish(),
  cover_url: z.string().nullish(),
  qr_code_id: z.string().nullish(),
  // Preferências de apresentação (JSONB do servidor) — inclui `theme`, a
  // preferência claro/escuro que viaja entre dispositivos (THEME-SYSTEM §5).
  preferences: z.record(z.unknown()).nullish(),
  // ── Cartão de visita permanente (funciona dentro e FORA do evento) ──
  skills: z.string().nullish(),          // competências (separadas por vírgula)
  whatsapp: z.string().nullish(),
  website: z.string().nullish(),
  linkedin: z.string().nullish(),
  twitter: z.string().nullish(),         // X
  instagram: z.string().nullish(),
  facebook: z.string().nullish(),
  github: z.string().nullish(),
  behance: z.string().nullish(),
  portfolio_url: z.string().nullish(),
  cv_url: z.string().nullish(),
  pitch_url: z.string().nullish(),
  // SSOT do onboarding (BD, não localStorage): null → ainda não fez o wizard.
  onboarded_at: z.string().nullish(),
}).passthrough();
export type Me = z.infer<typeof meSchema>;

/** Utilizador autenticado (Clerk → UUID interno) → GET /users/me. */
export async function getMe(): Promise<Me> {
  // Bypasses backend se estivermos em modo Preview (Tailscale) ou Mocks.
  // Falsificamos um utilizador incompleto para forçar a demonstração do Onboarding.
  if (env.preview || env.mocksEnabled) {
    // Override de role só p/ QA visual do harness (localStorage 'marc:qa:role':
    // admin|organizer|participant). Default = participant (mantém a demo do onboarding).
    // Sem efeito em produção (env.preview/mocksEnabled são false lá).
    const qaRole = localStorage.getItem('marc:qa:role') || 'participant';
    const mockUser = {
      id: 'usr_mock_123',
      full_name: localStorage.getItem('marc:onboarding:done') ? 'Utilizador de Teste' : '',
      email: 'teste@marcianusexperience.com',
      role: qaRole,
      user_role: qaRole,
      onboarded_at: localStorage.getItem('marc:onboarding:done') ? new Date().toISOString() : null,
    };
    return meSchema.parse(mockUser);
  }

  const raw = await http.get<unknown>('/users/me');
  return meSchema.parse(raw);
}

// ── Métricas globais do user (SSOT: /users/me/metrics) — MESMO valor em toda a
// app. 100% DB-driven. Em preview/mocks devolve null (UI mostra "—", nunca fake).
const metricsSchema = z.object({
  connections:     z.coerce.number().nullish(),
  taps:            z.coerce.number().nullish(),
  missions:        z.coerce.number().nullish(),
  active_missions: z.coerce.number().nullish(),
  impact_index:    z.coerce.number().nullish(),
  compatibility_avg: z.coerce.number().nullish(),
  // Progressão (gamificação) — XP real + nível/tier da mesma curva do backend.
  xp_total:           z.coerce.number().nullish(),
  level:              z.coerce.number().nullish(),
  level_progress_pct: z.coerce.number().nullish(),
  next_level_xp:      z.coerce.number().nullish(),
  tier:               z.string().nullish(),
}).passthrough();
export type MyMetrics = z.infer<typeof metricsSchema>;

export async function getMyMetrics(): Promise<MyMetrics | null> {
  if (env.preview || env.mocksEnabled) return null;
  try {
    return metricsSchema.parse(await http.get<unknown>('/users/me/metrics'));
  } catch {
    return null;
  }
}

// Campos editáveis pelo próprio (whitelist do core: PATCH /users/me).
export interface MeUpdate {
  full_name?: string; job_title?: string; company?: string; sector?: string;
  bio?: string; location?: string; phone?: string; avatar_url?: string; username?: string;
  // Cartão de visita permanente — contactos, redes e recursos.
  skills?: string; whatsapp?: string; website?: string;
  linkedin?: string; twitter?: string; instagram?: string; facebook?: string; github?: string; behance?: string;
  portfolio_url?: string; cv_url?: string; pitch_url?: string;
}
export async function updateMe(patch: MeUpdate): Promise<Me> {
  if (env.preview || env.mocksEnabled) {
    return meSchema.parse({ id: 'usr_mock_123', ...patch });
  }
  const raw = await http.patch<unknown>('/users/me', patch);
  return meSchema.parse(raw);
}

// ── Foto de perfil (POST /users/me/avatar, multipart campo "file") ──
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024; // 5 MB (limite do backend)
export const AVATAR_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function validateAvatar(file: File): string | null {
  if (!AVATAR_MIME.includes(file.type)) return i18next.t('profile.avatarBadType', { ns: 'account' });
  if (file.size > AVATAR_MAX_BYTES) return i18next.t('profile.avatarTooBig', { ns: 'account' });
  return null;
}

export async function uploadAvatar(file: File): Promise<{ ok: boolean; avatar_url: string }> {
  if (env.preview || env.mocksEnabled) {
    return { ok: true, avatar_url: URL.createObjectURL(file) };
  }
  return http.upload<{ ok: boolean; avatar_url: string }>('/users/me/avatar', file);
}

// Foto de CAPA — mesmas regras/validação do avatar; editável a qualquer momento.
export async function uploadCover(file: File): Promise<{ ok: boolean; cover_url: string }> {
  if (env.preview || env.mocksEnabled) {
    return { ok: true, cover_url: URL.createObjectURL(file) };
  }
  return http.upload<{ ok: boolean; cover_url: string }>('/users/me/cover', file);
}

// ── Onboarding concluído (POST /users/me/onboarded) — grava objetivos nas
// preferences e marca onboarded_at (idempotente). É ISTO que impede o wizard de
// reaparecer noutro dispositivo — o localStorage é só cache local.
export async function markOnboarded(goals: string[] = []): Promise<void> {
  if (env.preview || env.mocksEnabled) return;
  await http.post('/users/me/onboarded', { goals });
}

// ── Tipo de conta (self-service, PATCH /users/me/role) — SÓ participant↔organizer.
// O backend rejeita qualquer outro valor (nunca admin) e bloqueia a equipa.
export type AccountType = 'participant' | 'organizer';
export async function updateMyRole(role: AccountType): Promise<{ ok: boolean; role: string }> {
  if (env.preview || env.mocksEnabled) return { ok: true, role };
  return http.patch<{ ok: boolean; role: string }>('/users/me/role', { role });
}

// ── Tema (PATCH /users/me/preferences) — a preferência claro/escuro viaja com
// a pessoa entre dispositivos. Fire-and-forget no chamador: a rede nunca
// decide a cor de um ecrã (o localStorage é a fonte da primeira pintura).
/** Idioma da interface — guardado no perfil para viajar entre dispositivos. */
export async function setLocalePreference(locale: 'pt' | 'en' | 'fr' | 'es' | null): Promise<void> {
  if (env.preview || env.mocksEnabled) return;
  await http.patch('/users/me/preferences', { locale });
}

export async function setThemePreference(theme: 'dark' | 'light' | null): Promise<void> {
  if (env.preview || env.mocksEnabled) return;
  await http.patch('/users/me/preferences', { theme });
}
